import { ChangeDetectionStrategy, Component, DestroyRef, ViewChild, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { SignaturePad, SignaturePadModule } from 'angular2-signaturepad';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';

import { BerthApiService } from '@bcmApiServices/berth.api-service';
import { BoatApiService } from '@bcmApiServices/boat.api-service';
import { BcmWorkflowApiService } from '@bcmApiServices/bcm-workflow-api.service';
import { CompanyApiService } from '@bcmApiServices/company.api-service';
import { PersonApiService } from '@bcmApiServices/person.api-service';
import { ProductsApiService } from '@bcmApiServices/product.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { TranslationModule } from '@core/translation/translation.module';
import { downloadFile } from '@shared/functions/download-file';
import { findKnownShortcodes, findUnknownShortcodes } from '@shared/functions/find-short-codes';
import { Berth } from '@shared/models/berth';
import { Boat } from '@shared/models/boat';
import { Company } from '@shared/models/company';
import { Contract, ContractFileType } from '@shared/models/contract';
import { Person } from '@shared/models/person';
import { Product } from '@shared/models/product';
import { BcmService } from '@modules/bcm/bcm.service';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bDialogComponent } from '@u2bUi/dialog/u2b-dialog.component';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bIndicatorCardComponent } from '@u2bUi/indicator-card/u2b-indicator-card.component';

import { missingContractFieldsCompany } from '@bcm-work-flows/contract-creator/missing-contract-fields-company';
import { missingContractFieldsPerson } from '@bcm-work-flows/contract-creator/missing-contract-fields-person';

import { BcmContractsStoreService } from '@bcmServices/contracts/bcm-contracts-store.service';

import { BookingV2BerthOption, BookingV2BoatOption, BookingV2PersonOption } from '../dtos/booking-v2.dtos';
import { buildContractCreationBody, buildCoOwnerForm, ContractCoOwner } from '../utils/booking-v2-contract-payload';

interface ProductRow {
    product: Product | null;
    quantity: number | null;
}

const BKZ_FIELDS = [
    'BKZ_BETRAG_NETTO', 'BKZ_MWST', 'BKZ_MWST_BETRAG', 'BKZ_BETRAG_GESAMT_NETTO',
    'BKZ_BETRAG_GESAMT_BRUTTO', 'BKZ_BETRAG_FAELLIG_ZUM', 'BKZ_TZ_1_BETRAG',
    'BKZ_TZ_2_BETRAG', 'BKZ_TZ_1_FAELLIG_ZUM', 'BKZ_TZ_2_FAELLIG_ZUM',
] as const;

type BkzForm = Record<(typeof BKZ_FIELDS)[number], unknown>;

function emptyBkz(): BkzForm {
    return BKZ_FIELDS.reduce((acc, key) => ({ ...acc, [key]: null }), {} as BkzForm);
}

function emptyCoOwners(): ContractCoOwner[] {
    return Array.from({ length: 4 }, () => ({ name: '', str: '', plz: '', ort: '' }));
}

/**
 * PrimeNG port of the legacy ContractCreatorComponent for the booking-v2 wizard.
 * Full parity: template pick + date range + boat/berth, co-owner (VMI_), BKZ,
 * products, unknown placeholders, output options (mail/PDF/Word template),
 * signature, and a missing-data warning — all reusing the same backend
 * (`workflows/contract-creation`) and shortcode/missing-field utilities.
 */
@Component({
    selector: 'booking-v2-contract-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule, Button, Checkbox, DatePicker, Dialog, InputNumber, InputText, RadioButton, Select,
        ToggleSwitch, SignaturePadModule, TranslationModule,
        U2bDateMaskDirective, U2bDialogComponent, U2bIconButtonComponent, U2bIndicatorCardComponent,
    ],
    templateUrl: './booking-v2-contract-dialog.component.html',
})
export class BookingV2ContractDialogComponent {

    private readonly contractsStore = inject(BcmContractsStoreService);
    private readonly workflowApi = inject(BcmWorkflowApiService);
    private readonly productsApi = inject(ProductsApiService);
    private readonly personApi = inject(PersonApiService);
    private readonly companyApi = inject(CompanyApiService);
    private readonly boatApi = inject(BoatApiService);
    private readonly berthApi = inject(BerthApiService);
    private readonly bcmService = inject(BcmService);
    private readonly notifications = inject(AppNotificationService);
    private readonly destroyRef = inject(DestroyRef);

    @ViewChild(SignaturePad) private signaturePad?: SignaturePad;

    // Seed data from the saved booking (edit mode) or the post-save flow.
    readonly bookingId = input<number | null>(null);
    readonly person = input.required<BookingV2PersonOption>();
    readonly boat = input<BookingV2BoatOption | null>(null);
    readonly berths = input<readonly BookingV2BerthOption[]>([]);
    readonly fromDate = input<Date | null>(null);
    readonly toDate = input<Date | null>(null);

    readonly visible = model<boolean>(false);
    readonly created = output<void>();

    readonly ContractFileType = ContractFileType;

    readonly contracts = toSignal(this.contractsStore.contracts$, { initialValue: [] as Contract[] });
    readonly products = toSignal(this.productsApi.getAll().pipe(catchError(() => of<Product[]>([]))), { initialValue: [] as Product[] });

    // ── Core ──
    readonly selectedContract = signal<Contract | null>(null);
    readonly from = signal<Date | null>(null);
    readonly to = signal<Date | null>(null);
    readonly selectedBoatId = signal<number | null>(null);
    readonly selectedBerthId = signal<number | null>(null);

    // ── Output ──
    readonly downloadPdf = signal<boolean>(true);
    readonly sendMail = signal<boolean>(false);
    readonly contractFileType = signal<ContractFileType>(ContractFileType.MAIL);

    // ── Co-owner (VMI_) ──
    readonly captureCoOwner = signal<boolean>(false);
    readonly coOwners = signal<ContractCoOwner[]>(emptyCoOwners());
    readonly activeCoOwner = signal<number>(0);

    // ── BKZ ──
    readonly captureBkz = signal<boolean>(false);
    readonly bkz = signal<BkzForm>(emptyBkz());
    readonly bkzProduct = signal<Product | null>(null);

    // ── Products ──
    readonly captureProducts = signal<boolean>(false);
    readonly productRows = signal<ProductRow[]>([{ product: null, quantity: null }]);

    // ── Unknown placeholders ──
    readonly unknownValues = signal<Record<string, string>>({});

    // ── Signature ──
    readonly signEnabled = signal<boolean>(false);
    readonly signMode = signal<'draw' | 'text'>('draw');
    readonly signatureText = signal<string>('');
    readonly signaturePadOptions = {
        canvasWidth: 460 * Math.max(window.devicePixelRatio || 1, 1),
        canvasHeight: 150 * Math.max(window.devicePixelRatio || 1, 1),
    };

    readonly isSaving = signal<boolean>(false);

    // Full models fetched for the missing-data pre-check (v2 facade only holds options).
    private readonly fullPerson = signal<Person | null>(null);
    private readonly fullCompany = signal<Company | null>(null);
    private readonly fullBoat = signal<Boat | null>(null);
    private readonly fullBerth = signal<Berth | null>(null);

    // Only real, persisted boats can go on a contract (manual/new drafts have id < 0).
    readonly boatOption = computed<BookingV2BoatOption | null>(() => {
        const b = this.boat();
        return b && b.id > 0 ? b : null;
    });

    // Tenant-relation ids of the booking party — used to group templates into
    // "matching" vs "other", mirroring the legacy autocomplete grouping.
    private readonly tenantRelationIds = computed<number[]>(() => {
        const source = this.person().kind === 'company' ? this.fullCompany() : this.fullPerson();
        return (source?.activeAndNextTenantRelationAssignments ?? []).map(tr => tr.tenantRelation?.id).filter((id): id is number => id != null);
    });

    readonly groupedContracts = computed(() => {
        const relIds = this.tenantRelationIds();
        const matching: Contract[] = [];
        const other: Contract[] = [];
        for (const c of this.contracts()) {
            const isMatch = (c.tenantRelations ?? []).some(tr => relIds.includes(tr.id));
            (isMatch ? matching : other).push(c);
        }
        const groups: Array<{ label: string; items: Contract[] }> = [];
        if (matching.length) {
            groups.push({ label: TolgeeService.translate('bookingV2.contract.groupMatching'), items: matching });
        }
        if (other.length) {
            groups.push({ label: TolgeeService.translate('bookingV2.contract.groupOther'), items: other });
        }
        return groups;
    });

    readonly knownShortCodes = computed<string[]>(() => findKnownShortcodes(this.selectedContract()?.content ?? ''));
    readonly unknownShortCodes = computed<string[]>(() => findUnknownShortcodes(this.selectedContract()?.content ?? ''));

    readonly hasBoatCodes = computed(() => this.knownShortCodes().some(c => c.includes('BOOT_')));
    readonly hasBerthCodes = computed(() => this.knownShortCodes().some(c => c.includes('LP_')));
    readonly hasCoOwnerCodes = computed(() => this.knownShortCodes().some(c => c.includes('VMI_')));
    readonly hasBkzCodes = computed(() => this.knownShortCodes().some(c => c.includes('BKZ_')));
    readonly hasProductCodes = computed(() => this.knownShortCodes().some(c => c.includes('PRODUKT')));

    // p-select needs a mutable array; the input is readonly.
    readonly berthOptions = computed(() => [...this.berths()]);

    readonly personMail = computed(() => this.person().mail);

    // Word templates with separate mail/print files let the user choose which to render.
    readonly showFileTypeRadio = computed(() => {
        const c = this.selectedContract();
        return !!c?.isWordTemplate && !!c?.hasDifferentTemplates && !this.sendMail();
    });

    readonly contractTemplateMissing = computed(() => {
        const c = this.selectedContract();
        return !!c?.isWordTemplate && !c?.templateMailFile && !c?.templatePrintFile;
    });

    // A contract that references the berth needs one picked before it can be built.
    readonly berthRequiredButMissing = computed(() =>
        this.hasBerthCodes() && this.berths().length > 0 && this.selectedBerthId() == null,
    );

    readonly productsIncomplete = computed(() =>
        this.captureProducts() && this.productRows().some(r => !r.product || r.quantity == null),
    );

    readonly canSubmit = computed(() =>
        !this.isSaving()
        && this.selectedContract() != null
        && !this.contractTemplateMissing()
        && !this.berthRequiredButMissing()
        && !this.productsIncomplete(),
    );

    // Missing required data for the picked template — reuses the legacy pure helpers.
    readonly missingFields = computed<{ tenant: string[]; person: string[]; company: string[]; boat: string[]; berth: string[] }>(() => {
        const empty = { tenant: [], person: [], company: [], boat: [], berth: [] };
        if (!this.selectedContract()) {
            return empty;
        }
        const codes = this.knownShortCodes();
        const tenant = this.bcmService.tenant;
        const boat = this.fullBoat() ?? undefined;
        const berth = this.fullBerth() ?? undefined;
        if (this.person().kind === 'company') {
            const company = this.fullCompany();
            if (!company) { return empty; }
            const r = missingContractFieldsCompany(codes, tenant, company, boat as Boat, berth as Berth);
            return { ...empty, ...r };
        }
        const p = this.fullPerson();
        if (!p) { return empty; }
        const r = missingContractFieldsPerson(codes, tenant, p, boat as Boat, berth as Berth);
        return { ...empty, ...r };
    });

    readonly hasMissingFields = computed(() => {
        const m = this.missingFields();
        return m.tenant.length + m.person.length + m.company.length + m.boat.length + m.berth.length > 0;
    });

    readonly missingGroupOrder = ['tenant', 'person', 'company', 'boat', 'berth'] as const;

    missingGroup(group: string): string[] {
        return (this.missingFields() as Record<string, string[]>)[group] ?? [];
    }

    constructor() {
        // Re-seed and fetch full models every time the dialog opens.
        effect(() => {
            if (!this.visible()) {
                return;
            }
            this.resetForm();
            this.fetchFullModels();
        });
    }

    private resetForm(): void {
        this.selectedContract.set(null);
        this.from.set(this.fromDate());
        this.to.set(this.toDate());
        this.selectedBoatId.set(this.boatOption()?.id ?? null);
        const berths = this.berths();
        this.selectedBerthId.set(berths.length === 1 ? berths[0].id : null);
        this.downloadPdf.set(true);
        this.sendMail.set(false);
        this.contractFileType.set(ContractFileType.MAIL);
        this.captureCoOwner.set(false);
        this.coOwners.set(emptyCoOwners());
        this.activeCoOwner.set(0);
        this.captureBkz.set(false);
        this.bkz.set(emptyBkz());
        this.bkzProduct.set(null);
        this.captureProducts.set(false);
        this.productRows.set([{ product: null, quantity: null }]);
        this.unknownValues.set({});
        this.signEnabled.set(false);
        this.signMode.set('draw');
        this.signatureText.set('');
    }

    private fetchFullModels(): void {
        this.fullPerson.set(null);
        this.fullCompany.set(null);
        this.fullBoat.set(null);
        this.fullBerth.set(null);

        const p = this.person();
        const person$ = p.kind === 'person'
            ? this.personApi.getOne(p.id).pipe(catchError(() => of(null)))
            : of(null);
        const company$ = p.kind === 'company'
            ? this.companyApi.getOne(p.id).pipe(catchError(() => of(null)))
            : of(null);
        const boatId = this.boatOption()?.id ?? null;
        const boat$ = boatId ? this.boatApi.getOne(boatId).pipe(catchError(() => of(null))) : of(null);

        forkJoin({ person: person$, company: company$, boat: boat$ })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ person, company, boat }) => {
                this.fullPerson.set(person);
                this.fullCompany.set(company);
                this.fullBoat.set(boat);
            });
    }

    // Berth is fetched on selection (the id is only known after the user picks).
    onBerthChange(id: number | null): void {
        this.selectedBerthId.set(id);
        if (id == null) {
            this.fullBerth.set(null);
            return;
        }
        this.berthApi.getOne(id)
            .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
            .subscribe(berth => this.fullBerth.set(berth));
    }

    close(): void {
        this.visible.set(false);
    }

    // ── Co-owner helpers ──
    updateCoOwner(index: number, field: keyof ContractCoOwner, value: string): void {
        this.coOwners.update(list => list.map((c, i) => i === index ? { ...c, [field]: value } : c));
    }

    // ── BKZ helpers ──
    setBkz(field: (typeof BKZ_FIELDS)[number], value: unknown): void {
        this.bkz.update(current => ({ ...current, [field]: value }));
    }

    onBkzProductChange(product: Product | null): void {
        this.bkzProduct.set(product);
        if (product) {
            this.bkz.update(current => ({
                ...current,
                BKZ_BETRAG_NETTO: product.price,
                BKZ_MWST: product.taxRate?.value ?? null,
                BKZ_BETRAG_GESAMT_NETTO: product.price,
                BKZ_BETRAG_GESAMT_BRUTTO: product.price,
            }));
        }
    }

    // ── Product row helpers ──
    addProductRow(): void {
        this.productRows.update(rows => [...rows, { product: null, quantity: null }]);
    }

    removeProductRow(index: number): void {
        this.productRows.update(rows => rows.length > 1 ? rows.filter((_, i) => i !== index) : rows);
    }

    setProductRow(index: number, patch: Partial<ProductRow>): void {
        this.productRows.update(rows => rows.map((r, i) => i === index ? { ...r, ...patch } : r));
    }

    // ── Unknown placeholder helpers ──
    setUnknown(code: string, value: string): void {
        this.unknownValues.update(current => ({ ...current, [code]: value }));
    }

    // ── Signature helpers ──
    clearSignature(): void {
        this.signaturePad?.clear();
    }

    private buildSignPayload(): Record<string, unknown> | undefined {
        if (!this.signEnabled()) {
            return undefined;
        }
        if (this.signMode() === 'draw') {
            const dataUrl = this.signaturePad && !this.signaturePad.isEmpty() ? this.signaturePad.toDataURL() : null;
            return { sign: true, signature: dataUrl, signatureText: null };
        }
        return { sign: true, signature: null, signatureText: this.signatureText().trim() || null };
    }

    submit(preview: boolean): void {
        const contract = this.selectedContract();
        if (!contract || !this.canSubmit()) {
            return;
        }
        this.isSaving.set(true);

        const products = this.productRows()
            .filter(r => r.product && r.quantity != null)
            .map(r => ({ product: r.product, quantity: r.quantity }));

        const body = buildContractCreationBody({
            preview,
            bookingId: this.bookingId(),
            contractId: contract.id,
            person: { id: this.person().id, kind: this.person().kind },
            // Sent whenever selected (mirrors the legacy form values), not gated on
            // shortcodes — the backend simply ignores unused entity data.
            boatId: this.selectedBoatId(),
            berthId: this.selectedBerthId(),
            fromDate: this.from(),
            toDate: this.to(),
            sendMail: this.sendMail(),
            downloadPdf: this.downloadPdf(),
            contractFileType: this.contractFileType(),
            contractCoOwnerForm: this.captureCoOwner() ? buildCoOwnerForm(this.coOwners()) : undefined,
            contractBuildingCostSubsidyForm: this.captureBkz() ? { ...this.bkz() } : undefined,
            productsForm: this.captureProducts() ? { products } : undefined,
            unknownShortCodes: this.buildUnknownPayload(),
            sign: this.buildSignPayload(),
        });

        this.workflowApi.contractCreation$(body)
            .pipe(finalize(() => this.isSaving.set(false)))
            .subscribe({
                next: (file) => {
                    this.notifications.showSuccess(TolgeeService.translate('bookingV2.contract.notifications.created'));
                    if (this.downloadPdf()) {
                        downloadFile(file, contract.title, 'pdf');
                    }
                    if (!preview) {
                        this.created.emit();
                        this.close();
                    }
                },
                error: () => this.notifications.showError(TolgeeService.translate('bookingV2.contract.notifications.error')),
            });
    }

    private buildUnknownPayload(): Record<string, string> {
        const values = this.unknownValues();
        // Only send placeholders the current template actually has.
        return this.unknownShortCodes().reduce((acc, code) => {
            acc[code] = values[code] ?? '';
            return acc;
        }, {} as Record<string, string>);
    }
}
